import React, { useState, useRef, useEffect } from "react";
import { Select, Typography, message } from "antd";
import imageCompression from "browser-image-compression";
import { platformApi } from "../../../../api";

const { Option } = Select;
const { Title, Text } = Typography;

const rtoDocuments = [
    { key: "address_proof", label: "Address Proof", subcategories: ["Aadhar Card", "Rental Agreement", "Driving License", "Electricity Bill", "House Tax Bill", "LIC Policy", "Passport", "Photo ID Proof", "Ration Card", "Voter ID", "Employees Identification card issued by Central/State Government"] },
    { key: "form_22", label: "Form 22", subcategories: ["Form 22 Road Worthiness certificate and body"] },
    { key: "form_21", label: "Form 21", subcategories: ["Form 21 Sales Certificate BS6 Only"] },
    { key: "form_14", label: "Form 14", subcategories: ["Form 14 Tax assessment"] },
    { key: "inspection_certificate", label: "Inspection Certificate", subcategories: ["PHOTO"] },
    { key: "invoice", label: "Invoice", subcategories: ["Vehicle Tax Invoice"] },
    { key: "pan_card_form_60", label: "Pan Card/Form 60", subcategories: ["PAN card", "Form 60"] },
    { key: "signature", label: "Signature", subcategories: ["Purchaser Signature in Form 20"] },
    { key: "disclaimer", label: "Disclaimer", subcategories: ["Disclaimer"] },
];

const UploadIcon = () => (
    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
);

const ChevronLeft = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const ChevronRight = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const TrashIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" />
    </svg>
);

export default function RTODocumentModal({ open = true, close, data }) {
    console.log("RTODocumentModal Data:", data);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [documentsState, setDocumentsState] = useState(
        rtoDocuments.map((doc) => ({
            ...doc,
            selectedSubcategory: null,
            fileUrl: null,
            fileId: null, // db id
        }))
    );
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);

    // Effect to pre-populate existing documents
    useEffect(() => {
        if (data) {
            setDocumentsState(prev => {
                const newState = [...prev];

                // 1. Pre-fill from gatepassData (overlapping photos from "Upload Photo")
                if (data.gatepassData) {
                    const gatepassMapping = {
                        // These are now handled in "Upload Photo" model, 
                        // so we don't need to map them here anymore as they were removed from rtoDocuments.
                    };
                    data.gatepassData.forEach(gDoc => {
                        const targetKey = gatepassMapping[gDoc.type];
                        if (targetKey) {
                            const idx = newState.findIndex(d => d.key === targetKey);
                            if (idx !== -1) {
                                newState[idx] = {
                                    ...newState[idx],
                                    fileUrl: gDoc.url,
                                    fileId: gDoc.id,
                                    selectedSubcategory: newState[idx].subcategories[0],
                                    isGatepassSource: true
                                };
                            }
                        }
                    });
                }

                // 2. Override/Fill with actual rtoDocuments (from backend)
                if (data.rtoDocuments) {
                    data.rtoDocuments.forEach(doc => {
                        const idx = newState.findIndex(d => d.key === doc.type);
                        if (idx !== -1) {
                            newState[idx] = {
                                ...newState[idx],
                                fileUrl: doc.url,
                                fileId: doc.id,
                                selectedSubcategory: doc.subcategory,
                                isGatepassSource: false
                            };
                        }
                    });
                }
                return newState;
            });
        }
    }, [data]);

    const currentDoc = rtoDocuments[currentIndex];
    if (!currentDoc) return null;
    const currentState = documentsState[currentIndex];

    const updateState = (updates) => {
        setDocumentsState((prev) => {
            const next = [...prev];
            next[currentIndex] = { ...next[currentIndex], ...updates };
            return next;
        });
    };

    const handleSubcategoryChange = (value) => {
        updateState({ selectedSubcategory: value });
        setDropdownOpen(false);
    };

    const handleFile = async (file) => {
        if (!file) return;
        if (!currentState.selectedSubcategory) {
            message.warning("Please select a Sub Category first");
            return;
        }

        try {
            setUploading(true);

            // 🧠 Frontend Image Compression (Same as ImageUploader)
            let fileToUpload = file;
            const isImage = file.type.startsWith("image/");
            if (isImage && file.size / 1024 > 400) {
                try {
                    const options = {
                        maxSizeMB: 0.4,
                        maxWidthOrHeight: 1920,
                        initialQuality: 0.8,
                        useWebWorker: true,
                    };
                    fileToUpload = await imageCompression(file, options);
                } catch (err) {
                    console.error("Image compression failed:", err);
                }
            }

            const formData = new FormData();
            formData.append("profile", fileToUpload); // multer expects 'profile' based on route definition
            formData.append("id", data.id); // saleRegisterId
            formData.append("type", currentDoc.key);
            formData.append("subcategory", currentState.selectedSubcategory);

            const res = await platformApi.post("/api/upload/rto", formData);
            if (res && res.data && res.data.code === 200) {
                const uploaded = res.data.response.data;
                updateState({
                    fileUrl: uploaded.url,
                    fileId: uploaded.id
                });
                message.success(`${currentDoc.label} uploaded successfully`);
            }
        } catch (err) {
            console.error("Upload error:", err);
            message.error("Failed to upload document");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentState.fileId) return;

        // If it's from Gatepass (Upload Photo flow), just clear it locally if desired, 
        // but we shouldn't delete the actual VehicleDocument from here.
        if (currentState.isGatepassSource) {
            updateState({ fileUrl: null, fileId: null, isGatepassSource: false });
            message.info("Photo removed from RTO view (original photo still exists in Upload Photo)");
            return;
        }

        try {
            message.loading({ content: "Deleting...", key: "delete-rto" });
            const res = await platformApi.post("/api/upload/deleteRtoFile", {
                id: currentState.fileId,
                url: currentState.fileUrl
            });

            if (res && res.data && res.data.code === 200) {
                updateState({ fileUrl: null, fileId: null, isGatepassSource: false });
                message.success({ content: "Document deleted", key: "delete-rto" });
            }
        } catch (err) {
            console.error("Delete error:", err);
            message.error({ content: "Failed to delete document", key: "delete-rto" });
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const handleFileInput = (e) => {
        const file = e.target.files[0];
        if (file) handleFile(file);
    };

    const moveForward = () => {
        if (currentIndex < rtoDocuments.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setDropdownOpen(false);
        }
    };

    const moveBackward = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setDropdownOpen(false);
        }
    };

    const progressPercent = ((currentIndex + 1) / rtoDocuments.length) * 100;

    if (!open) return null;

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

        .rto-overlay {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          font-family: 'DM Sans', sans-serif;
        }

        .rto-modal {
          background: #ffffff;
          width: 96vw; max-width: 1000px;
          height: 85vh; max-height: 560px;
          border-radius: 8px;
          box-shadow: 0 16px 48px rgba(0,0,0,0.24), 0 0 0 1px rgba(0,0,0,0.06);
          display: flex; flex-direction: column;
          overflow: hidden;
        }

        /* Header — following GatepassModal pattern */
        .rto-header {
          background: #607d8b;
          padding: 12px 24px;
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .rto-title {
          color: #fff;
          font-size: 20px; font-weight: 500;
          margin: 0;
        }
        .rto-header-meta {
          display: flex; gap: 40px; align-items: center;
        }
        .rto-meta-item {
          color: rgba(255,255,255,0.9);
          font-size: 14px;
          font-weight: normal;
        }
        .rto-meta-item strong { color: #fff; font-weight: bold; margin-right: 6px; }
        .rto-close-btn {
          background: rgba(255,255,255,0.15);
          border: none; color: #fff;
          width: 28px; height: 28px;
          border-radius: 50%; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; transition: background 0.15s;
        }
        .rto-close-btn:hover { background: rgba(255,255,255,0.25); }

        /* Merged Search & Counter Strip */
        .rto-toolbar {
          background: #f1f3f4;
          padding: 8px 16px 8px 24px;
          display: flex; align-items: center; justify-content: space-between;
          border-bottom: 1px solid #cfd8dc;
          gap: 12px;
        }
        .rto-toolbar-left { display: flex; align-items: center; gap: 12px; }
        .rto-toolbar-right { display: flex; align-items: center; gap: 12px; }
        
        .rto-search-label { font-size: 11px; font-weight: 600; color: #78909c; text-transform: uppercase; letter-spacing: 0.05em; }
        .rto-counter-text {
          font-size: 12px; color: #546e7a; font-weight: 600;
          background: #cfd8dc; padding: 4px 14px; border-radius: 20px;
          font-family: 'DM Sans', sans-serif;
        }

        /* Progress bar */
        .rto-progress-bar-track {
          height: 3px; background: #cfd8dc; flex-shrink: 0;
        }
        .rto-progress-bar-fill {
          height: 100%; background: #455a64;
          transition: width 0.3s ease;
        }

        /* Body */
        .rto-body {
          flex: 1; display: flex; align-items: stretch;
          overflow: hidden; position: relative;
        }

        /* Nav arrows */
        .rto-nav-btn {
          width: 60px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: transparent; border: none; cursor: pointer;
          color: #607d8b;
          transition: background 0.15s, color 0.15s;
        }
        .rto-nav-btn:hover:not(:disabled) {
          background: #f1f3f4; color: #37474f;
        }
        .rto-nav-btn:disabled { color: #cfd8dc; cursor: default; }
        .rto-nav-btn .nav-circle {
          width: 42px; height: 42px; border-radius: 50%;
          border: 2px solid currentColor;
          display: flex; align-items: center; justify-content: center;
          transition: transform 0.1s;
        }
        .rto-nav-btn:active:not(:disabled) .nav-circle { transform: scale(0.95); }

        /* Content area */
        .rto-content {
          flex: 1; padding: 32px 40px;
          display: flex; flex-direction: column; gap: 32px;
        }

        .rto-selection-row {
          display: flex; gap: 24px; width: 100%;
        }

        /* Field labels and inputs */
        .rto-field-block {
          flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 0;
        }
        .rto-field-label {
          font-size: 11px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.1em; color: #78909c;
        }
        .rto-doc-type-input {
          background: #f1f3f4;
          border: 1px solid #cfd8dc;
          border-radius: 6px;
          padding: 12px 16px;
          font-size: 14px; font-weight: 600;
          color: #455a64;
          font-family: 'DM Sans', sans-serif;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        /* Subcategory dropdown */
        .rto-dropdown-wrapper {
          flex: 1; display: flex; flex-direction: column; gap: 8px;
          position: relative; min-width: 0;
        }
        .rto-dropdown-trigger {
          background: #fff;
          border: 1px solid #cfd8dc;
          border-radius: 6px;
          padding: 12px 16px;
          font-size: 14px; color: #455a64;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          display: flex; align-items: center; justify-content: space-between;
          transition: border-color 0.15s;
        }
        .rto-dropdown-trigger:hover, .rto-dropdown-trigger.open {
          border-color: #607d8b;
        }
        .rto-dropdown-trigger span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .rto-dropdown-trigger .placeholder { color: #b0bec5; }
        .rto-chevron {
          flex-shrink: 0; margin-left: 10px;
          transition: transform 0.2s;
          color: #90a4ae;
        }
        .rto-chevron.open { transform: rotate(180deg); }
        .rto-dropdown-menu {
          position: absolute; top: calc(100% + 5px); left: 0; right: 0;
          background: #fff;
          border: 1px solid #cfd8dc;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          z-index: 999;
          max-height: 200px;
          overflow-y: auto;
        }
        .rto-dropdown-item {
          padding: 12px 16px;
          font-size: 13px; color: #455a64;
          cursor: pointer;
          border-bottom: 1px solid #f1f3f4;
        }
        .rto-dropdown-item:hover { background: #f1f3f4; }
        .rto-dropdown-item.selected { background: #eceff1; color: #37474f; font-weight: 600; }

        /* Upload area centered */
        .rto-upload-container {
          display: flex; justify-content: center; width: 100%;
        }
        .rto-upload-block {
          width: 100%; max-width: 600px;
          display: flex; flex-direction: column; gap: 8px;
        }
        .rto-upload-zone {
          border: 2px dashed #b0bec5;
          border-radius: 12px;
          min-height: 200px;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s;
          background: #fafbfc;
          position: relative; overflow: hidden;
          padding: 24px;
        }
        .rto-upload-zone:hover, .rto-upload-zone.dragging {
          border-color: #607d8b;
          background: #f1f3f4;
        }
        .rto-upload-zone .upload-icon { color: #90a4ae; }
        .rto-upload-zone .upload-hint {
          font-size: 13px; color: #78909c; text-align: center; line-height: 1.5;
        }
        .rto-upload-zone .upload-btn {
          background: #546e7a; color: #fff;
          border: none; border-radius: 6px;
          padding: 10px 24px; font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: 'DM Sans', sans-serif;
          transition: background 0.15s;
        }
        .rto-upload-zone .upload-btn:hover { background: #37474f; }
        
        .rto-preview {
          position: relative; width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px;
        }
        .rto-preview img {
          max-width: 100%; max-height: 140px;
          border-radius: 6px; object-fit: contain;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
        }
        .rto-preview-name {
          font-size: 13px; color: #455a64; font-weight: 600;
          text-align: center; padding: 0 16px;
          word-break: break-all;
        }
        .rto-delete-btn {
          position: absolute; top: -10px; right: -10px;
          background: #fff; border: 1px solid #ffcdd2;
          border-radius: 50%; color: #ef5350;
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; box-shadow: 0 3px 8px rgba(0,0,0,0.15);
          z-index: 10;
        }
        .rto-delete-btn:hover { background: #ef5350; color: #fff; border-color: #ef5350; }
      `}</style>

            <div className="rto-overlay" onClick={(e) => e.target === e.currentTarget && close?.()}>
                <div className="rto-modal">
                    {/* Main Header — Gatepass Form Style */}
                    <div className="rto-header">
                        <h2 className="rto-title">Upload RTO Documents</h2>
                        <div className="rto-header-meta">
                            <span className="rto-meta-item">
                                <strong>Customer:</strong> {data?.customer?.name || data?.customerName || "N/A"}
                            </span>
                            <span className="rto-meta-item">
                                <strong>Vehicle:</strong> {data?.vehicle?.modelName || data?.vehicleModel || "N/A"}
                            </span>
                        </div>
                        <button className="rto-close-btn" onClick={close}>×</button>
                    </div>

                    {/* Merged Toolbar: Search Left, Count Right */}
                    <div className="rto-toolbar">
                        <div className="rto-toolbar-left">

                            <Select
                                showSearch
                                placeholder="Search Document Type..."
                                optionFilterProp="children"
                                onSelect={(val) => {
                                    const idx = rtoDocuments.findIndex(d => d.key === val);
                                    if (idx !== -1) {
                                        setCurrentIndex(idx);
                                        setDropdownOpen(false);
                                    }
                                }}
                                style={{ width: 300 }}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {rtoDocuments.map(d => (
                                    <Option key={d.key} value={d.key}>{d.label}</Option>
                                ))}
                            </Select>
                        </div>
                        <div className="rto-toolbar-right">
                            <span className="rto-counter-text">
                                Document {currentIndex + 1} of {rtoDocuments.length}
                            </span>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="rto-progress-bar-track">
                        <div className="rto-progress-bar-fill" style={{ width: `${progressPercent}%` }} />
                    </div>

                    {/* Body */}
                    <div className="rto-body">
                        {/* Left nav */}
                        <button
                            className="rto-nav-btn"
                            onClick={moveBackward}
                            disabled={currentIndex === 0}
                            title="Previous Document"
                        >
                            <span className="nav-circle"><ChevronLeft /></span>
                        </button>

                        {/* Content */}
                        <div className="rto-content">

                            <div className="rto-selection-row">
                                {/* Document Type */}
                                <div className="rto-field-block">
                                    <div className="rto-field-label">Document Type</div>
                                    <div className="rto-doc-type-input">{currentDoc.label}</div>
                                </div>

                                {/* Sub Category */}
                                <div className="rto-dropdown-wrapper" ref={dropdownRef}>
                                    <div className="rto-field-label">Sub Category</div>
                                    <div
                                        className={`rto-dropdown-trigger${dropdownOpen ? " open" : ""}`}
                                        onClick={() => setDropdownOpen(!dropdownOpen)}
                                    >
                                        <span className={currentState.selectedSubcategory ? "" : "placeholder"}>
                                            {currentState.selectedSubcategory || "—— SELECT ——"}
                                        </span>
                                        <svg
                                            className={`rto-chevron${dropdownOpen ? " open" : ""}`}
                                            width="14" height="14" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2.5"
                                            strokeLinecap="round" strokeLinejoin="round"
                                        >
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </div>
                                    {dropdownOpen && (
                                        <div className="rto-dropdown-menu">
                                            {currentDoc.subcategories.map((sub) => (
                                                <div
                                                    key={sub}
                                                    className={`rto-dropdown-item${currentState.selectedSubcategory === sub ? " selected" : ""}`}
                                                    onClick={() => handleSubcategoryChange(sub)}
                                                >
                                                    {sub}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Upload Container centered */}
                            <div className="rto-upload-container">
                                <div className="rto-upload-block">
                                    <div className="rto-field-label">Upload Document</div>
                                    <div
                                        className={`rto-upload-zone${isDragging ? " dragging" : ""}${uploading ? " uploading" : ""}`}
                                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                        onDragLeave={() => setIsDragging(false)}
                                        onDrop={handleDrop}
                                        onClick={() => !currentState.fileUrl && !uploading && fileInputRef.current?.click()}
                                    >
                                        {uploading ? (
                                            <div className="rto-preview">
                                                <div className="upload-icon spinning">
                                                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#607d8b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="12" y1="2" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="22" /><line x1="4.93" y1="4.93" x2="7.76" y2="7.76" /><line x1="16.24" y1="16.24" x2="19.07" y2="19.07" /><line x1="2" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="22" y2="12" /><line x1="4.93" y1="19.07" x2="7.76" y2="16.24" /><line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                                                    </svg>
                                                </div>
                                                <div className="upload-hint">Uploading...</div>
                                            </div>
                                        ) : currentState.fileUrl ? (
                                            <div className="rto-preview">
                                                {currentState.isGatepassSource && (
                                                    <div style={{
                                                        position: "absolute",
                                                        top: "10px",
                                                        left: "10px",
                                                        background: "rgba(96, 125, 139, 0.9)",
                                                        color: "white",
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                        fontSize: "10px",
                                                        fontWeight: "600",
                                                        zIndex: 5,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        From Upload Photo
                                                    </div>
                                                )}
                                                {currentState.fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                                    <img src={currentState.fileUrl} alt="Preview" />
                                                ) : (
                                                    <div className="rto-preview-name">📄 {currentDoc.label}</div>
                                                )}
                                                <button
                                                    className="rto-delete-btn"
                                                    title={currentState.isGatepassSource ? "Remove from RTO view" : "Delete Document"}
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                                                >
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="upload-icon"><UploadIcon /></div>
                                                <div className="upload-hint">
                                                    Drag & drop file here<br />or click to browse
                                                </div>
                                                <button
                                                    className="upload-btn"
                                                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                                >
                                                    Choose File
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: "none" }}
                                        accept="image/*,.pdf"
                                        onChange={handleFileInput}
                                    />
                                </div>
                            </div>

                        </div>

                        {/* Right nav */}
                        <button
                            className="rto-nav-btn"
                            onClick={moveForward}
                            disabled={currentIndex === rtoDocuments.length - 1}
                            title="Next Document"
                        >
                            <span className="nav-circle"><ChevronRight /></span>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}